import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { captureAttribution } from '@lib/attribution';

/**
 * Captures marketing/referral attribution params (?ref=, utm_*, click IDs) into the
 * durable signup store (@lib/attribution) whenever the query string CHANGES during an
 * SPA session. The boot capture in index.tsx handles first paint; this catches links
 * opened after the app has already mounted (client-side navigations). Renders nothing.
 *
 * Deliberately keyed on `search` alone, NOT pathname/location.key: a pathname-only
 * navigation with the same lingering query string is the SAME inbound touch, and
 * re-firing would restamp captured_at and artificially extend the 30-day attribution
 * window without any new inbound link.
 *
 * AIDEV-NOTE: Must live inside RootLayout's provider tree - useLocation needs the router
 * context that only exists under <RootLayout/>. captureAttribution() reads window.location
 * and is idempotent, so double-firing with the boot capture is harmless.
 */
export const AttributionCapture = (): null => {
  const location = useLocation();
  const search = location.search;

  useEffect(() => {
    // Cheap bail: a search-less navigation can carry no tracked keys. When present,
    // captureAttribution() parses window.location and filters to tracked keys itself.
    if (!search) return;
    captureAttribution();
  }, [search]);

  return null;
};
